import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useChatStore } from "#app/store/chat";
import { createStreamTimer } from "#app/utils/stream-timer";
import { ensureAuthToken } from "./auth.client";
import { appendChatLocaleToFormData } from "./chat-notification-i18n";
import { streamErrorMessage } from "./spend-limit-message";
import { getWorkflowServiceContextHeaders } from "./workflow-svc";

export type ChatSSEOptions = {
  threadId: string;
  formData: FormData;
  onMessage?: (content: string) => void;
  onClose?: () => void;
  onError?: (error: any) => void;
  onOpen?: () => void;
  onWorkflowComplete?: () => void;
  // Optional inactivity timeout (ms). If provided and we are in design-time
  // execution, the stream will be aborted and UI state will be reset when
  // no 'message' or 'notification' arrives within this period.
  inactivityMs?: number;
  onTimeout?: () => void;
  headers?: Record<string, string>;
};

export type BuildFormDataOptions = {
  workflowId?: string | null;
  inputs: Record<string, any>;
  envs?: Record<string, any>;
  integrationOverrides?: Record<string, string>;
  model?: string | null;
  isInterrupt?: boolean;
  interruptType?: string | null;
  selectedWorkflowId?: string | null;
  selectedRuleId?: string | null;
  /** Ordered rule IDs for a rule-chain-select interrupt; sent as a JSON string. */
  selectedRuleIds?: string[] | null;
  /** User dismissed the interrupt popup — resume the graph with a cancel payload. */
  interruptCancelled?: boolean;
  message?: string;
  /**
   * Allow the executor to auto-retry a failed step. An execution parameter, so
   * it rides in the body next to inputs/envs — not in a header.
   */
  autoRetryAllowed?: boolean;
};

/**
 * Build multipart FormData matching AgenticWorkflowFormCanvas shape
 * - inputs[field] for scalars
 * - inputs[field][i] for arrays
 * - file(s) appended as binary parts
 */
export function buildMultipartFormData(
  options: BuildFormDataOptions,
): FormData {
  const {
    workflowId,
    inputs,
    envs,
    integrationOverrides,
    model,
    isInterrupt,
    interruptType,
    selectedWorkflowId,
    selectedRuleId,
    selectedRuleIds,
    interruptCancelled,
    message,
    autoRetryAllowed,
  } = options;
  const fd = new FormData();

  if (workflowId) fd.append("workflowId", String(workflowId));
  if (typeof isInterrupt !== "undefined")
    fd.append("isInterrupt", String(isInterrupt));
  if (interruptType) fd.append("interruptType", interruptType);
  if (selectedWorkflowId) fd.append("selectedWorkflowId", selectedWorkflowId);
  if (selectedRuleId) fd.append("selectedRuleId", selectedRuleId);
  if (selectedRuleIds && selectedRuleIds.length > 0)
    fd.append("selectedRuleIds", JSON.stringify(selectedRuleIds));
  if (interruptCancelled) fd.append("interruptCancelled", "true");
  if (typeof autoRetryAllowed !== "undefined")
    fd.append("autoRetryAllowed", String(autoRetryAllowed));
  if (model) fd.append("model", model);
  if (integrationOverrides && Object.keys(integrationOverrides).length > 0) {
    for (const [slug, accountId] of Object.entries(integrationOverrides)) {
      if (!accountId) continue;
      fd.append(`integration_overrides[${slug}]`, String(accountId));
    }
  }

  for (const [name, value] of Object.entries(inputs)) {
    if (value === undefined || value === null) continue;

    // Files
    if (value instanceof File) {
      fd.append(`inputs[${name}]`, value);
      continue;
    }
    if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
      (value as File[]).forEach((file, index) => {
        fd.append(`inputs[${name}][${index}]`, file);
      });
      continue;
    }

    // Arrays of primitives
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        fd.append(`inputs[${name}][${index}]`, String(item));
      });
      continue;
    }

    // Booleans / numbers / strings
    fd.append(
      `inputs[${name}]`,
      typeof value === "boolean" ? String(value) : String(value),
    );
  }

  if (message) {
    fd.append("message", message);
  }

  if (envs) {
    for (const [name, value] of Object.entries(envs)) {
      if (value === undefined || value === null) continue;
      fd.append(`envs[${name}]`, String(value));
    }
  }

  return fd;
}

/**
 * Centralized SSE chat submission handler.
 * Handles all standard events: message, workflow, workflow-run, activity-run, notification.
 */
export function streamChat(options: ChatSSEOptions) {
  const {
    threadId,
    formData,
    onMessage,
    onClose,
    onError,
    onOpen,
    onWorkflowComplete,
  } = options;

  appendChatLocaleToFormData(formData);

  // Create AbortController to allow interruption
  const controller = new AbortController();
  // Store controllers in a Map keyed by threadId so multiple sessions can coexist.
  if (!(window as any).sseControllers) {
    (window as any).sseControllers = new Map<
      string,
      { controller: AbortController; threadId: string }
    >();
  }
  (window as any).sseControllers.set(threadId, { controller, threadId });
  useChatStore.getState().addRunningSessionId(threadId);
  // Also keep the legacy single reference pointing to the LATEST controller
  (window as any).currentSSEController = {
    controller,
    threadId,
  };
  // Stable id for this request to prevent duplicate processing on retries/reconnects
  const idempotencyKey =
    crypto && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Modular live stream timer (displayed as a To-Do on the streaming assistant message)
  const streamTimer = createStreamTimer();

  // Inactivity watchdog (design-time only)
  let inactivityTimer: number | undefined;
  const shouldMonitorInactivity =
    typeof options.inactivityMs === "number" &&
    useChatStore.getState().isDesignTimeExecuting;

  const clearInactivityTimer = () => {
    if (typeof inactivityTimer !== "undefined") {
      clearTimeout(inactivityTimer);
      inactivityTimer = undefined as unknown as number;
    }
  };

  const handleInactivityTimeout = () => {
    try {
      console.warn("Design-time execution timed out due to inactivity");
      // Abort THIS stream.
      //
      // This previously called `.abort()` on `currentSSEController`, which is a
      // `{ controller, threadId }` wrapper with no such method. Being the first
      // statement of a try with no catch, the TypeError meant NONE of the resets
      // below ever ran — so the watchdog never actually recovered a stuck
      // design-time execution, which is the only thing it exists to do.
      //
      // Using the local `controller` rather than the global also matters: the
      // global always points at the LATEST stream, so on a second concurrent
      // session this would have aborted the wrong one.
      try {
        controller.abort();
      } catch {}
      const sseMap = (window as any).sseControllers as
        | Map<string, { controller: AbortController }>
        | undefined;
      if (sseMap?.get(threadId)?.controller === controller) {
        sseMap.delete(threadId);
      }
      if ((window as any).currentSSEController?.controller === controller) {
        (window as any).currentSSEController = null;
      }
      // Without this the thread stays in `runningSessionIds`, so the composer
      // keeps queueing messages against a stream we just killed.
      try {
        useChatStore.getState().removeRunningSessionId(threadId);
      } catch {}

      // Reset UI/store state related to execution
      const store = useChatStore.getState();
      try {
        store.setWorkflowRunStatus("failed", null);
      } catch {}
      try {
        store.updateChatStore({ isStreaming: false, formMode: null });
      } catch {}
      try {
        store.setIsDesignTimeExecuting(false);
      } catch {}
      try {
        store.setCurrentThinking(null);
      } catch {}

      // Finalize timer as failed due to timeout
      try {
        streamTimer.stop("failed");
      } catch {}
      // The turn was abandoned, so nothing is still in progress.
      try {
        store.completeTodosOnLastAssistantMessage(false);
      } catch {}

      // Notify caller if provided
      options.onTimeout?.();
      options.onClose?.();
    } finally {
      clearInactivityTimer();
    }
  };

  const resetInactivityTimer = () => {
    if (!shouldMonitorInactivity) return;
    clearInactivityTimer();
    inactivityTimer = window.setTimeout(
      handleInactivityTimeout,
      options.inactivityMs as number,
    );
  };

  const safeParseEventData = (raw: string) => {
    try {
      return JSON.parse(raw) as any;
    } catch {
      return undefined;
    }
  };

  // All SSE event types use a "content" field for the payload; message events stream text chunks in content.
  const getMessageChunk = (payload: any): string => {
    if (payload == null) return "";
    if (typeof payload === "string") return payload;
    const content = payload.content;
    const message = payload.message;
    if (typeof content === "string") return content;
    if (typeof message === "string") return message;
    return "";
  };

  // Throttle message chunks to one per animation frame so the UI paints between updates
  // (avoids React batching when many SSE events arrive in one tick)
  const messageChunkBuffer: string[] = [];
  let messageDrainScheduled = false;
  const isTabHidden = () => typeof document !== "undefined" && document.hidden;
  const drainMessageChunks = () => {
    // requestAnimationFrame is frozen while the tab is hidden, which stalled this
    // drain loop entirely — chunks piled up in the buffer and the message only
    // appeared (one frame at a time) once the user came back. Nothing is painting
    // while hidden, so per-frame pacing buys nothing there: deliver everything.
    if (isTabHidden()) {
      flushMessageChunkBuffer();
      return;
    }
    if (messageChunkBuffer.length === 0) {
      messageDrainScheduled = false;
      return;
    }
    const chunk = messageChunkBuffer.shift()!;
    onMessage?.(chunk);
    messageDrainScheduled = true;
    requestAnimationFrame(drainMessageChunks);
  };
  const flushMessageChunkBuffer = () => {
    while (messageChunkBuffer.length > 0) {
      const chunk = messageChunkBuffer.shift()!;
      onMessage?.(chunk);
    }
    messageDrainScheduled = false;
  };

  /**
   * Ensure there is a streaming assistant row to attach chunks / to-dos to.
   * Prefer reopening the last real assistant turn while this SSE is alive —
   * minting a fresh empty row after a premature finalize is what produced a
   * second bubble for the orchestrator's post-tool follow-up.
   */
  const ensureStreamingAssistant = () => {
    const store = useChatStore.getState();
    if (store.messages.some((m) => m.role === "assistant" && m.isStreaming)) {
      return;
    }
    const messages = [...store.messages];
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]!;
      if (msg.role !== "assistant") continue;
      if (msg.extra?.widget) continue;
      if (msg.extra?.isNotification) continue;
      messages[i] = { ...msg, isStreaming: true };
      store.updateChatStore({ messages, isStreaming: true });
      return;
    }
    store.addMessage({
      content: "",
      role: "assistant",
      isStreaming: true,
    });
  };

  // The check inside drainMessageChunks only helps chunks that arrive AFTER the
  // tab is hidden. If the tab hides while a drain is already scheduled, the
  // pending rAF never fires and `messageDrainScheduled` stays true, so no later
  // chunk can restart the loop. Flush on hide, and restart on return.
  const onVisibilityChangeWhileStreaming = () => {
    if (isTabHidden()) {
      flushMessageChunkBuffer();
    } else if (messageChunkBuffer.length > 0 && !messageDrainScheduled) {
      drainMessageChunks();
    }
  };
  const detachVisibilityListener = () => {
    if (typeof document === "undefined") return;
    document.removeEventListener(
      "visibilitychange",
      onVisibilityChangeWhileStreaming,
    );
  };
  if (typeof document !== "undefined") {
    document.addEventListener(
      "visibilitychange",
      onVisibilityChangeWhileStreaming,
    );
    // onclose/onerror are not guaranteed to run on an explicit abort
    // (stopStream, session teardown), so hang cleanup off the signal too.
    controller.signal.addEventListener("abort", detachVisibilityListener, {
      once: true,
    });
  }

  /** A `activity-run` event: a step's run status and outputs for the activity timeline. */
  const handleActivityRunEvent = (ev: { data: string }) => {
    const raw = safeParseEventData(ev.data) ?? {};
    const content = raw.content ?? {};

    const store = useChatStore.getState();
    store.setLastStreamEvent({
      type: "activity-run",
      payload: content,
    });
    // Ensure form stays disabled for activity-run event
    if (store.isWaitingForInput) {
      store.updateChatStore({
        isWaitingForInput: false,
        currentInterrupt: null,
        interruptsQueue: [],
      });
    }

    const toId = (value: unknown) => {
      if (typeof value === "string" && value.trim().length > 0) {
        return value.trim();
      }
      if (typeof value === "number" && Number.isFinite(value)) {
        return String(value);
      }
      return undefined;
    };

    const activityRunId =
      toId(content.activityRunId) ?? toId(content.activity_run_id);
    if (!activityRunId) return;

    const incomingStepId =
      toId(content.stepId) ?? toId(content.step_id) ?? undefined;

    const incomingKind: "step" | "condition" =
      content.kind === "condition" ? "condition" : "step";
    const incomingConditionId =
      toId(content.conditionId) ?? toId(content.condition_id);
    const incomingConditionName =
      typeof content.conditionName === "string"
        ? content.conditionName
        : typeof content.condition_name === "string"
          ? content.condition_name
          : undefined;

    const statusRawSource =
      typeof content.status === "string"
        ? content.status
        : typeof content.state === "string"
          ? content.state
          : typeof content.eventType === "string"
            ? content.eventType
            : null;
    const statusRaw = statusRawSource ? statusRawSource.toLowerCase() : "";
    const attempt =
      typeof content.attempt === "number"
        ? content.attempt
        : typeof content.attempt === "string"
          ? parseInt(content.attempt, 10)
          : undefined;
    const baseStatus:
      | "running"
      | "completed"
      | "failed"
      | "waiting"
      | "timeout" =
      statusRaw === "completed"
        ? "completed"
        : statusRaw === "failed"
          ? "failed"
          : statusRaw === "waiting"
            ? "waiting"
            : statusRaw === "timeout"
              ? "timeout"
              : "running";
    // If status is completed, always show completed (even after retries)
    // If status is waiting, preserve it for the debug panel
    // If status is timeout, preserve it too — it is terminal, and a
    // timed-out step carries attempts, so falling through to the
    // attempt logic below would relabel it "retry".
    let normalizedStatus:
      | "running"
      | "completed"
      | "failed"
      | "retry"
      | "waiting"
      | "timeout" =
      baseStatus === "completed"
        ? "completed"
        : baseStatus === "waiting"
          ? "waiting"
          : baseStatus === "timeout"
            ? "timeout"
            : attempt !== undefined && !Number.isNaN(attempt)
              ? attempt > 10
                ? "failed"
                : attempt > 1
                  ? "retry"
                  : baseStatus
              : baseStatus;

    const parseTimestamp = (input: any) => {
      if (typeof input === "number" && !Number.isNaN(input)) {
        return new Date(input).toISOString();
      }
      if (typeof input === "string" && input.trim().length > 0) {
        const date = new Date(input);
        if (!Number.isNaN(date.getTime())) {
          return date.toISOString();
        }
      }
      return undefined;
    };
    const baselineStatus = useChatStore.getState().activityRunStatus || [];

    // Prefer strict identity by activityRunId. When activityRunId
    // is missing on either side, fall back to (stepId, kind,
    // conditionId) matching so a condition event doesn't collapse
    // onto its parent step's row (both share `stepId` but the
    // condition carries a non-null `conditionId`).
    const existingIndex = baselineStatus.findIndex((item) => {
      // 1. Strict identity match by activityRunId (both must have it)
      if (activityRunId && item.activityRunId) {
        return item.activityRunId === activityRunId;
      }
      // 2. Fallback: match by (stepId, kind, conditionId) for upsert.
      //    This covers cases where one side has activityRunId and the
      //    other doesn't (e.g., notification-created entries).
      if (!incomingStepId || item.stepId !== incomingStepId) {
        return false;
      }
      // Ensure kind matches to prevent step/condition cross-contamination
      const itemKind = item.kind ?? "step";
      if (itemKind !== incomingKind) {
        return false;
      }
      const itemConditionId = item.conditionId ?? null;
      const eventConditionId = incomingConditionId ?? null;
      return itemConditionId === eventConditionId;
    });
    const previousItem =
      existingIndex >= 0 ? baselineStatus[existingIndex] : undefined;

    const nowIso = new Date().toISOString();
    const eventStartedAt =
      parseTimestamp(
        content.startedAt ??
          content.started_at ??
          content.createdAt ??
          content.created_at,
      ) ?? undefined;
    const eventUpdatedAt =
      parseTimestamp(
        content.updatedAt ??
          content.updated_at ??
          content.completedAt ??
          content.completed_at ??
          content.timestamp ??
          content.time,
      ) ?? undefined;

    const outputs =
      normalizedStatus === "retry" ||
      (normalizedStatus === "running" && attempt && attempt > 1)
        ? (content.outputs ?? undefined)
        : (content.outputs ??
          content.output ??
          previousItem?.outputs ??
          undefined);

    // Guard: Do not downgrade a terminal status to running/retry
    // (Out-of-order event protection)
    // Note: We intentionally DO NOT block `failed` -> `running` because
    // manual retries need to transition a failed step back to running.
    if (
      previousItem?.status === "completed" &&
      (normalizedStatus === "running" || normalizedStatus === "retry")
    ) {
      // Ignore the stale retry/running event if we already completed
      return;
    }

    let outputObjectKeys: string[] | undefined = previousItem?.outputObjectKeys;
    if (Array.isArray(content.outputObjectKeys)) {
      outputObjectKeys = content.outputObjectKeys as string[];
    } else if (Array.isArray(content.output_object_keys)) {
      outputObjectKeys = content.output_object_keys as string[];
    }

    const error =
      typeof content.error === "string"
        ? content.error
        : (previousItem?.error ?? undefined);

    // Human-review submission summary attached by the workflow
    // service when a reviewer approves/rejects a form; see
    // `activity-runs.ts` terminal-status publish. Keep the
    // previous value if a later event doesn't carry one (e.g.
    // a retry signal) so the timeline doesn't flash back to
    // the plain "Outputs" view.
    const userForm =
      content.userForm && typeof content.userForm === "object"
        ? content.userForm
        : previousItem?.userForm;

    const newActivityRun = {
      stepId: incomingStepId ?? previousItem?.stepId ?? activityRunId ?? "",
      activityRunId: activityRunId ?? previousItem?.activityRunId,
      kind: incomingKind ?? previousItem?.kind ?? "step",
      conditionId: incomingConditionId ?? previousItem?.conditionId,
      conditionName: incomingConditionName ?? previousItem?.conditionName,
      status: normalizedStatus,
      attempt:
        attempt !== undefined && !Number.isNaN(attempt)
          ? attempt
          : previousItem?.attempt,
      outputs,
      outputObjectKeys,
      error,
      startedAt:
        previousItem?.startedAt ??
        eventStartedAt ??
        previousItem?.updatedAt ??
        nowIso,
      updatedAt: eventUpdatedAt ?? nowIso,
      userForm,
    };

    let updatedStatus;
    if (existingIndex >= 0) {
      updatedStatus = [...baselineStatus];
      updatedStatus[existingIndex] = newActivityRun;
    } else {
      updatedStatus = [...baselineStatus, newActivityRun];
    }

    // When a condition branch completes as taken (conditionMet === true),
    // proactively mark all sibling condition branches on the same step
    // as completed with conditionMet === false. This lets the UI suppress
    // not-taken branches immediately rather than waiting for potentially
    // delayed or missing backend events.
    if (
      incomingKind === "condition" &&
      normalizedStatus === "completed" &&
      outputs?.conditionMet === true &&
      incomingStepId
    ) {
      for (let i = 0; i < updatedStatus.length; i++) {
        const sibling = updatedStatus[i];
        if (
          sibling?.kind === "condition" &&
          sibling.stepId === incomingStepId &&
          sibling.conditionId !== incomingConditionId &&
          sibling.status !== "completed"
        ) {
          updatedStatus[i] = {
            ...sibling,
            status: "completed" as const,
            outputs: { ...sibling.outputs, conditionMet: false },
            updatedAt: eventUpdatedAt ?? nowIso,
          };
        }
      }
    }

    useChatStore.getState().setActivityRunStatus(updatedStatus);
  };

  /** A `notification` event: a planner or run notification for the chat. */
  const handleNotificationEvent = (ev: { data: string }) => {
    const data = safeParseEventData(ev.data) ?? {};

    // Set last stream event for notification (all events use content for payload)
    useChatStore.getState().setLastStreamEvent({
      type: "notification",
      payload: data,
    });

    const notificationId =
      typeof data.id === "string" ? data.id : crypto.randomUUID();

    const raw =
      typeof data.content === "string"
        ? data.content.trim()
        : String(data.content ?? "").trim();

    // Extract status: use "started", "completed", "failed" as-is from notification
    const explicitStatus =
      typeof data.status === "string" ? data.status.trim().toLowerCase() : null;
    const status = (() => {
      if (explicitStatus === "completed") return "completed";
      if (explicitStatus === "failed") return "failed";
      if (explicitStatus === "started") return "started";
      // Fallback: infer from content text
      const lowered = raw.toLowerCase();
      if (lowered.includes("completed") || lowered.includes("done"))
        return "completed";
      if (lowered.includes("failed") || lowered.includes("error"))
        return "failed";
      if (lowered.includes("started") || lowered.includes("begin"))
        return "started";
      return "started";
    })();

    // Update todos and thinking text in store
    const store = useChatStore.getState();

    // Ensure there is a streaming assistant message to attach to-dos to
    try {
      ensureStreamingAssistant();
    } catch {}

    // Update thinking text with latest notification content
    store.setCurrentThinking(raw);
    // Add/update todo on the streaming assistant message
    try {
      store.addOrUpdateTodoOnLastAssistantMessage(
        raw,
        status as any,
        notificationId,
      );
    } catch {}

    // Derive step status from notifications like:
    // "Working on: {step-name}" or "Finished: {step-name}"
    try {
      const wf = store.plannedWorkflow;
      if (wf && Array.isArray(wf.steps) && wf.steps.length > 0) {
        const text = raw.toLowerCase();
        const matchWorking = text.match(
          /^\s*(working\s*on|configuring)\s*[:\-]\s*(.+?)\s*$/i,
        );
        const matchFinished = text.match(
          /^\s*(finished|done|completed)\s*[:\-]\s*(.+?)\s*$/i,
        );
        let matchedStepName: string | null = null;
        let mappedStatus: "running" | "completed" | null = null;
        if (matchWorking && matchWorking[2]) {
          matchedStepName = matchWorking[2].replace(/^['"`\s]+|['"`\s]+$/g, "");
          mappedStatus = "running";
        } else if (matchFinished && matchFinished[2]) {
          matchedStepName = matchFinished[2].replace(
            /^['"`\s]+|['"`\s]+$/g,
            "",
          );
          mappedStatus = "completed";
        }

        if (matchedStepName && mappedStatus) {
          // Find step by name (case-insensitive, forgiving spacing/punctuation)
          const normalize = (s: string) =>
            s
              .toString()
              .trim()
              .toLowerCase()
              .replace(/\s+/g, " ")
              .replace(/[\-_:]+/g, " ");
          const target = normalize(matchedStepName);
          const step = wf.steps.find(
            (st) => normalize(st.name || "") === target,
          );
          if (step?.id) {
            const currentStatus = store.activityRunStatus || [];
            // Notifications ("Working on/Configuring/Finished")
            // are emitted only for step rows on the backend, so
            // restrict matching to step-kind entries to avoid
            // accidentally overwriting a condition row that
            // shares this `stepId`.
            const idx = currentStatus.findIndex(
              (it) => it.stepId === step.id && (it.kind ?? "step") === "step",
            );
            const updated = [...currentStatus];
            const nextItem = {
              stepId: step.id,
              status: mappedStatus,
              // Always preserve existing outputs so notification
              // events ("Working on …") don't wipe displayData
              // (e.g. the subworkflow URL) stored by an earlier
              // activity-run SSE event.
              outputs: updated[idx]?.outputs || undefined,
              // Preserve activityRunId so subsequent activity-run
              // SSE events can still match by identity instead of
              // falling back to stepId matching (avoids duplicates).
              activityRunId: updated[idx]?.activityRunId,
            } as {
              stepId: string;
              activityRunId?: string;
              status: "running" | "completed" | "failed";
              outputs?: any;
            };
            if (idx >= 0) updated[idx] = nextItem;
            else updated.push(nextItem);
            store.setActivityRunStatus(updated);
          }
        }
      }
    } catch {}
  };

  void (async () => {
    const token = await ensureAuthToken({ minValidityMs: 60_000 });
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getWorkflowServiceContextHeaders(),
      // Help servers implement idempotency;
      "Idempotency-Key": idempotencyKey,
      ...options.headers,
    };

    void fetchEventSource(
      `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/chats/${threadId}/messages`,
      {
        method: "POST",
        headers,
        body: formData,
        signal: controller.signal,
        // Keep the stream open when the tab is hidden to avoid background disconnects
        openWhenHidden: true,
        onmessage(ev) {
          try {
            const store = useChatStore.getState();
            if (store.currentSessionId !== threadId) {
              // Silently skip events for a different session.
              // Do NOT abort — the backend orchestrator needs this connection alive.
              return;
            }
            if (shouldMonitorInactivity) {
              const eventName = (ev.event ?? "message").toLowerCase();
              if (
                eventName === "message" ||
                eventName === "notification" ||
                eventName === "thinking" ||
                eventName === "workflow" ||
                eventName === "workflow-run" ||
                eventName === "activity-run" ||
                eventName === "interrupt" ||
                eventName === "resume" ||
                eventName === "unconnected-integrations" ||
                eventName === "unconnected-integration" ||
                eventName === "ping"
              ) {
                resetInactivityTimer();
              }
            }
            if (ev.event === "message" && ev.data) {
              const data = safeParseEventData(ev.data);
              const chunk = getMessageChunk(data ?? {});
              if (chunk.length === 0) return;
              // Clear thinking state when actual message content arrives
              const store = useChatStore.getState();
              if (store.currentThinking) {
                store.setCurrentThinking(null);
              }
              store.setLastStreamEvent({ type: "message", payload: chunk });
              // Deliver one chunk per frame so the UI paints between updates
              messageChunkBuffer.push(chunk);
              if (!messageDrainScheduled) drainMessageChunks();
            } else if (ev.event === "workflow" && ev.data) {
              const data = safeParseEventData(ev.data) ?? {};
              const content = data.content;
              const store = useChatStore.getState();
              store.setLastStreamEvent({
                type: "workflow",
                payload: content,
              });
              store.setPlannedWorkflow(content);
              // Ensure form stays disabled for workflow event
              if (store.isWaitingForInput) {
                store.updateChatStore({
                  isWaitingForInput: false,
                  currentInterrupt: null,
                  interruptsQueue: [],
                });
              }
            } else if (ev.event === "attach-trigger" && ev.data) {
              const data = safeParseEventData(ev.data) ?? {};
              const content = data.content;
              if (content) {
                const store = useChatStore.getState();
                // Clear any lingering "thinking" line so it doesn't render as a
                // stale artifact just above the attach widget.
                if (store.currentThinking) {
                  store.setCurrentThinking(null);
                }
                store.appendWidgetMessage({
                  type: "attach-trigger",
                  data: content,
                });
              }
            } else if (ev.event === "workflow-run" && ev.data) {
              const data = safeParseEventData(ev.data) ?? {};
              const payload = data.content ?? data;
              const statusText =
                typeof payload?.status === "string"
                  ? payload.status.toLowerCase()
                  : "";
              const normalizedStatus:
                | "running"
                | "failed"
                | "completed"
                | "cancelled"
                | "waiting"
                | "timeout" =
                statusText === "completed"
                  ? "completed"
                  : statusText === "cancelled" || statusText === "canceled"
                    ? "cancelled"
                    : statusText === "waiting"
                      ? "waiting"
                      : statusText === "failed"
                        ? "failed"
                        : // Terminal. Everything unrecognised falls through to
                          // "running" below, which would leave a run that stopped
                          // waiting looking live for the rest of the session.
                          statusText === "timeout"
                          ? "timeout"
                          : "running";
              const rawRunId =
                payload?.workflowRunId ??
                payload?.workflow_run_id ??
                payload?.workflowRun?.id ??
                payload?.workflow_run?.id ??
                payload?.runId ??
                payload?.run_id ??
                payload?.id;
              const normalizedRunId =
                typeof rawRunId === "string"
                  ? rawRunId
                  : typeof rawRunId === "number" && Number.isFinite(rawRunId)
                    ? String(rawRunId)
                    : undefined;

              const store = useChatStore.getState();
              store.setLastStreamEvent({
                type: "workflow-run",
                payload,
              });
              store.setWorkflowRunStatus(normalizedStatus, normalizedRunId);

              // The workflow service forwards run inputs on the initial
              // "running" event so the design-time audit timeline can render
              // its Inputs panel without a separate fetch. Defensively handle
              // both array and JSON-string shapes (the bun-sql + Drizzle
              // writer encodes jsonb arrays as strings).
              const rawInputs = (payload as { inputs?: unknown } | undefined)
                ?.inputs;
              if (rawInputs !== undefined && rawInputs !== null) {
                let parsedInputs: unknown = rawInputs;
                if (typeof parsedInputs === "string") {
                  try {
                    parsedInputs = JSON.parse(parsedInputs);
                  } catch {
                    parsedInputs = [];
                  }
                }
                if (Array.isArray(parsedInputs)) {
                  store.setRunInputs(parsedInputs as never);
                }
              }

              // Handle design-time workflow completion
              if (
                payload.exit === true ||
                payload.status === "completed" ||
                payload.status === "failed" ||
                // Not a success, so `success` below stays false and the timer
                // stops as "failed" — but it must stop.
                payload.status === "timeout"
              ) {
                console.log("Design-time workflow completed:", payload);
                // Mark todos as completed/failed and clear thinking
                const success = payload.status === "completed";
                try {
                  streamTimer.stop(success ? "completed" : "failed");
                } catch {}
                try {
                  store.completeTodosOnLastAssistantMessage(success);
                } catch {}
                store.setCurrentThinking(null);
                // Reset waiting for input state when workflow completes
                store.updateChatStore({
                  isWaitingForInput: false,
                  currentInterrupt: null,
                  interruptsQueue: [],
                });
                clearInactivityTimer();
                onWorkflowComplete?.();
              }
            } else if (ev.event === "activity-run" && ev.data) {
              handleActivityRunEvent(ev);
            } else if (ev.event === "notification" && ev.data) {
              handleNotificationEvent(ev);
            } else if (ev.event === "thinking" && ev.data) {
              const data = safeParseEventData(ev.data) ?? {};
              const store = useChatStore.getState();
              const thinkingText =
                typeof data.content === "string"
                  ? data.content.trim()
                  : String(data.content ?? "").trim();
              if (!thinkingText) return;

              store.setLastStreamEvent({
                type: "thinking",
                payload: data,
              });

              // Keep streaming placeholder visible and update the current thinking line.
              try {
                ensureStreamingAssistant();
              } catch {}

              store.setCurrentThinking(thinkingText);
              try {
                store.addOrUpdateTodoOnLastAssistantMessage(
                  thinkingText,
                  "started",
                  typeof data.id === "string" ? data.id : undefined,
                );
              } catch {}
            } else if (ev.event === "interrupt") {
              const parsed = ev.data ? safeParseEventData(ev.data) : undefined;
              console.log(
                "🔔 Interrupt event received, enabling form...",
                parsed,
              );
              const store = useChatStore.getState();
              const newInterrupt = parsed?.content ?? parsed;

              // Prevent duplicates in queue
              const queue = [...(store.interruptsQueue || [])];
              const isDuplicate = (item: any) => {
                if (!item || !newInterrupt) return false;
                return JSON.stringify(item) === JSON.stringify(newInterrupt);
              };

              const isAlreadyCurrent = isDuplicate(store.currentInterrupt);
              const isAlreadyInQueue = queue.some(isDuplicate);

              if (!isAlreadyCurrent && !isAlreadyInQueue) {
                queue.push(newInterrupt);
              }

              // Pop the next interrupt if current is null
              let nextInterrupt = store.currentInterrupt;
              if (!nextInterrupt && queue.length > 0) {
                nextInterrupt = queue.shift();
              }

              store.setLastStreamEvent({
                type: "interrupt",
                payload: newInterrupt,
              });

              const shouldBlockExecution =
                store.isTryMode &&
                store.remainingTries !== null &&
                store.remainingTries <= 0;

              store.updateChatStore({
                formMode: "execution",
                isDesignTimeExecuting: false,
                currentInterrupt: shouldBlockExecution ? null : nextInterrupt,
                interruptsQueue: shouldBlockExecution ? [] : queue,
                isWaitingForInput: !shouldBlockExecution,
              });

              if (shouldBlockExecution) {
                store.addMessage({
                  role: "assistant",
                  content: `I'm sorry, but you have exhausted your free executions for this workflow in Try Mode. Please onboard me to your workspace to continue executing!\n\n<quick_replies>["Onboard me"]</quick_replies>`,
                });
                console.log("❌ Execution blocked - Try mode limit reached");
              } else {
                console.log("✅ Form enabled - isWaitingForInput set to true");
              }
              // Reset workflow run status
              store.setWorkflowRunStatus("completed", null);
              // Stop timer as interrupted
              try {
                streamTimer.stop("failed");
              } catch {}
            } else if (ev.event === "message-saved" && ev.data) {
              const parsed = safeParseEventData(ev.data);
              const messageId = parsed?.messageId;
              if (typeof messageId === "string") {
                useChatStore.getState().adoptSavedMessageIds({
                  messageId,
                  userMessageId:
                    typeof parsed?.userMessageId === "string"
                      ? parsed.userMessageId
                      : undefined,
                });
              }
            } else if (ev.event === "error") {
              // Previously fell through to the "unknown event type" arm below,
              // which console.logs and sets `lastStreamEvent` -- so a stream that
              // failed showed the sent message and then nothing at all. Silence
              // is indistinguishable from the product being broken, which is
              // exactly what a spend refusal was mistaken for.
              const parsed = ev.data ? safeParseEventData(ev.data) : undefined;
              const store = useChatStore.getState();
              store.updateChatStore({
                isLoading: false,
                isWaitingForInput: false,
                error: streamErrorMessage(parsed),
              });
              try {
                streamTimer.stop("failed");
              } catch {}
            } else if (
              ev.event === "unconnected-integrations" ||
              ev.event === "unconnected-integration"
            ) {
              const parsed = ev.data ? safeParseEventData(ev.data) : undefined;
              const raw = parsed?.content ?? parsed;
              const content = Array.isArray(raw)
                ? raw
                : raw != null
                  ? [raw]
                  : [];
              const payload = {
                content: content.map((c: any) => ({
                  slug: c?.slug ?? c?.name ?? "",
                  displayName:
                    c?.displayName ??
                    c?.display_name ??
                    c?.slug ??
                    c?.name ??
                    "Integration",
                })),
              };
              const store = useChatStore.getState();
              store.setLastStreamEvent({
                type: "unconnected-integrations",
                payload,
              });
              if (payload.content.length > 0) {
                store.setOpenUnconnectedIntegrationsModal(payload);
              }
            } else {
              // Handle any other unknown event type
              // Ensure form stays disabled for any event not explicitly handled
              const store = useChatStore.getState();
              if (ev.event && ev.event !== "ping") {
                console.log(`Received unknown event type: ${ev.event}`);
                store.setLastStreamEvent({
                  type: ev.event as string,
                  payload: ev.data
                    ? (safeParseEventData(ev.data) ?? ev.data)
                    : null,
                });
              }
              // Ensure form is disabled for any unknown event
              if (store.isWaitingForInput) {
                store.updateChatStore({
                  isWaitingForInput: false,
                  currentInterrupt: null,
                  interruptsQueue: [],
                });
              }
            }
          } catch (e) {
            console.error("Failed to parse SSE message", e);
          }
        },
        onerror(err) {
          console.error("SSE error", err);
          clearInactivityTimer();
          const currentSSE = (window as any).currentSSEController;
          if (
            currentSSE === controller ||
            currentSSE?.controller === controller
          ) {
            (window as any).currentSSEController = null;
          }
          // Clean up the Map entry
          const sseMap = (window as any).sseControllers as
            | Map<string, any>
            | undefined;
          if (sseMap) {
            sseMap.delete(threadId);
            useChatStore.getState().removeRunningSessionId(threadId);
          }
          // Note: DO NOT reset isWaitingForInput here - if an interrupt was received,
          // the form should stay enabled even if there's an error
          // Stop timer as failed
          try {
            streamTimer.stop("failed");
          } catch {}
          flushMessageChunkBuffer();
          detachVisibilityListener();
          const store = useChatStore.getState();
          if (store.currentSessionId === threadId) {
            // Same reasoning as `onclose`, including the session guard: the stream
            // is finished so no to-do can still be running. `failed`, since we got
            // here via an error.
            try {
              store.completeTodosOnLastAssistantMessage(false);
            } catch {}
            onError?.(err);
          }
          throw err;
        },
        onclose() {
          console.log("SSE closed");
          const store = useChatStore.getState();
          if (store.currentSessionId === threadId && store.isWaitingForInput) {
            console.log(
              "⏸️  SSE closed but form remains enabled (waiting for user input)",
            );
          }
          clearInactivityTimer();
          const currentSSEClose = (window as any).currentSSEController;
          if (
            currentSSEClose === controller ||
            currentSSEClose?.controller === controller
          ) {
            (window as any).currentSSEController = null;
          }
          // Clean up the Map entry
          const sseMapClose = (window as any).sseControllers as
            | Map<string, any>
            | undefined;
          if (sseMapClose) {
            sseMapClose.delete(threadId);
            useChatStore.getState().removeRunningSessionId(threadId);
          }
          // Note: DO NOT reset isWaitingForInput here - if an interrupt was received,
          // the form should stay enabled until the user submits it
          // The form will be disabled only when:
          // 1. User submits the form
          // 2. Workflow completes/fails
          // Stop further retries/reconnects to avoid duplicate POSTs
          try {
            controller.abort();
          } catch {}
          // Mark timer as completed if still running
          try {
            streamTimer.stop("completed");
          } catch {}
          flushMessageChunkBuffer();
          detachVisibilityListener();
          if (store.currentSessionId === threadId) {
            // The stream being over does NOT always mean the work is over. When
            // the run interrupts for user input the orchestrator closes this
            // connection and parks, so "Waiting for your inputs" is genuinely
            // still in progress — marking it `completed` here drew a green tick
            // against a step the user had not done yet. Re-read the flag rather
            // than trusting the `store` snapshot taken at the top of onclose,
            // since the timer stop and buffer flush above can both emit.
            //
            // Otherwise, finalise: only the design-time `workflow-run` branch
            // above used to do it, so a plain chat turn left every to-do in
            // `started` — they kept spinning long after the answer had arrived.
            //
            // Inside the session guard on purpose: `store.messages` holds only the
            // thread on screen, so finalising from a background stream would flip
            // to-dos on whatever thread the user had navigated to instead.
            if (!useChatStore.getState().isWaitingForInput) {
              try {
                store.completeTodosOnLastAssistantMessage(true);
              } catch {}
            }
            onClose?.();
          }
        },
        async onopen() {
          const store = useChatStore.getState();
          if (store.currentSessionId !== threadId) {
            // Silently keep running — don't abort, the orchestrator needs this.
            return;
          }
          console.log("SSE open");
          onOpen?.();
          resetInactivityTimer();
          // Ensure there is a streaming assistant message as soon as the stream is opened
          try {
            ensureStreamingAssistant();
            // Set initial "Thinking..." state when stream opens
            store.setCurrentThinking("Thinking...");
          } catch {}
          // Start the live timer To-Do
          streamTimer.start();
        },
      },
    );
  })();
}

export type SubscribeToWorkflowRunOptions = {
  runId: string;
  threadId?: string;
  onMessage?: (content: string) => void;
  onActivityRun?: (data: any) => void;
  onWorkflowRun?: (data: any) => void;
  onClose?: () => void;
  onError?: (error: any) => void;
};

export function subscribeToWorkflowRun(options: SubscribeToWorkflowRunOptions) {
  const {
    runId,
    threadId,
    onMessage,
    onActivityRun,
    onWorkflowRun,
    onClose,
    onError,
  } = options;

  const controller = new AbortController();
  (window as any).currentWorkflowRunController = {
    controller,
    runId,
  };

  const safeParseEventData = (raw: string) => {
    try {
      return JSON.parse(raw) as any;
    } catch {
      return undefined;
    }
  };

  const getMessageChunk = (payload: any): string => {
    if (payload == null) return "";
    if (typeof payload === "string") return payload;
    const content = payload.content;
    const message = payload.message;
    if (typeof content === "string") return content;
    if (typeof message === "string") return message;
    return "";
  };

  const messageChunkBuffer: string[] = [];
  let messageDrainScheduled = false;
  const drainMessageChunks = () => {
    if (messageChunkBuffer.length === 0) {
      messageDrainScheduled = false;
      return;
    }
    const chunk = messageChunkBuffer.shift()!;
    onMessage?.(chunk);
    messageDrainScheduled = true;
    requestAnimationFrame(drainMessageChunks);
  };
  const flushMessageChunkBuffer = () => {
    while (messageChunkBuffer.length > 0) {
      const chunk = messageChunkBuffer.shift()!;
      onMessage?.(chunk);
    }
    messageDrainScheduled = false;
  };

  void (async () => {
    let url = `/api/v1/workflow-runs/${runId}/stream`;
    if (threadId) {
      url += `?threadId=${encodeURIComponent(threadId)}`;
    }

    void fetchEventSource(url, {
      method: "GET",
      // Org/team context must travel with the run stream: the run is scoped to
      // the caller's ACTIVE org, and without these the service resolves their
      // personal org and 404s. Matters for marketplace try runs, which live in
      // the user's selected org rather than the publisher's.
      headers: { ...getWorkflowServiceContextHeaders() },
      signal: controller.signal,
      openWhenHidden: true,
      onmessage(ev) {
        try {
          const eventName = (ev.event ?? "message").toLowerCase();
          const store = useChatStore.getState();

          if (eventName === "message" && ev.data) {
            const data = safeParseEventData(ev.data);
            const chunk = getMessageChunk(data ?? {});
            if (chunk.length === 0) return;
            if (store.currentThinking) {
              store.setCurrentThinking(null);
            }
            store.setLastStreamEvent({ type: "message", payload: chunk });
            messageChunkBuffer.push(chunk);
            if (!messageDrainScheduled) drainMessageChunks();
          } else if (eventName === "try-limit-exceeded" && ev.data) {
            // Free marketplace try allowance is exhausted. Surfaced as its own
            // event so the UI prompts ONBOARDING rather than showing a generic
            // execution failure or a billing upgrade.
            const data = safeParseEventData(ev.data) ?? {};
            const store = useChatStore.getState();
            store.updateChatStore({ remainingTries: 0 });
            store.addMessage({
              role: "assistant",
              content: `${data.message || "You have used all your free runs of this operator."}\n\n<quick_replies>["Onboard me"]</quick_replies>`,
            });
            store.setWorkflowRunStatus("failed", null);
          } else if (eventName === "workflow-run" && ev.data) {
            const data = safeParseEventData(ev.data) ?? {};
            store.setWorkflowRunStatus(data.status, runId);
            onWorkflowRun?.(data);
            if (
              data.status === "completed" ||
              data.status === "failed" ||
              data.status === "cancelled" ||
              data.status === "timeout"
            ) {
              flushMessageChunkBuffer();
            }
          } else if (eventName === "activity-run" && ev.data) {
            const data = safeParseEventData(ev.data) ?? {};
            // Merge the single activity-run event into the existing array
            const current = store.activityRunStatus ?? [];
            const activityRunId =
              typeof data.activityRunId === "string" &&
              data.activityRunId.trim().length > 0
                ? data.activityRunId.trim()
                : typeof data.activity_run_id === "string" &&
                    data.activity_run_id.trim().length > 0
                  ? data.activity_run_id.trim()
                  : undefined;
            const incomingStepId =
              typeof data.stepId === "string" && data.stepId.trim().length > 0
                ? data.stepId.trim()
                : typeof data.step_id === "string" &&
                    data.step_id.trim().length > 0
                  ? data.step_id.trim()
                  : undefined;
            const incomingKind: "step" | "condition" =
              data.kind === "condition" ? "condition" : "step";
            const incomingConditionId =
              typeof data.conditionId === "string" &&
              data.conditionId.trim().length > 0
                ? data.conditionId.trim()
                : typeof data.condition_id === "string" &&
                    data.condition_id.trim().length > 0
                  ? data.condition_id.trim()
                  : undefined;

            const idx = current.findIndex((item: any) => {
              if (activityRunId && item.activityRunId) {
                return item.activityRunId === activityRunId;
              }
              if (!incomingStepId || item.stepId !== incomingStepId) {
                return false;
              }
              const itemKind = item.kind ?? "step";
              if (itemKind !== incomingKind) {
                return false;
              }
              const itemConditionId = item.conditionId ?? null;
              const eventConditionId = incomingConditionId ?? null;
              return itemConditionId === eventConditionId;
            });

            const previousItem = idx >= 0 ? current[idx] : undefined;

            const mapped = {
              stepId:
                incomingStepId ?? previousItem?.stepId ?? activityRunId ?? "",
              activityRunId: activityRunId ?? previousItem?.activityRunId,
              status: data.status,
              attempt: data.attempt,
              outputs: data.outputs ?? previousItem?.outputs ?? undefined,
              outputObjectKeys:
                data.outputObjectKeys ?? previousItem?.outputObjectKeys,
              kind: incomingKind,
              conditionId: incomingConditionId ?? previousItem?.conditionId,
              conditionName:
                data.conditionName ??
                data.condition_name ??
                previousItem?.conditionName,
            };
            const updated = [...current];
            if (idx >= 0) {
              updated[idx] = mapped;
            } else {
              updated.push(mapped);
            }

            // When a condition branch completes as taken (conditionMet === true),
            // proactively mark all sibling condition branches on the same step
            // as completed with conditionMet === false.
            if (
              incomingKind === "condition" &&
              data.status === "completed" &&
              data.outputs?.conditionMet === true &&
              incomingStepId
            ) {
              for (let i = 0; i < updated.length; i++) {
                const sibling = updated[i];
                if (
                  sibling?.kind === "condition" &&
                  sibling.stepId === incomingStepId &&
                  sibling.conditionId !== incomingConditionId &&
                  sibling.status !== "completed"
                ) {
                  updated[i] = {
                    ...sibling,
                    status: "completed" as const,
                    outputs: { ...sibling.outputs, conditionMet: false },
                  };
                }
              }
            }

            store.setActivityRunStatus(updated);
            onActivityRun?.(data);
          } else if (eventName === "thinking" && ev.data) {
            const data = safeParseEventData(ev.data) ?? {};
            store.setCurrentThinking(
              data.content ?? data.message ?? "Thinking...",
            );
          }
        } catch (err) {
          console.error("Error processing workflow-run SSE event:", err);
        }
      },
      onclose() {
        flushMessageChunkBuffer();
        if (
          (window as any).currentWorkflowRunController?.controller ===
          controller
        ) {
          (window as any).currentWorkflowRunController = null;
        }
        onClose?.();
      },
      onerror(err) {
        flushMessageChunkBuffer();
        console.error("Workflow run SSE error:", err);
        onError?.(err);
        throw err; // Stop retrying
      },
    });
  })();

  return {
    abort: () => {
      flushMessageChunkBuffer();
      controller.abort();
      if (
        (window as any).currentWorkflowRunController?.controller === controller
      ) {
        (window as any).currentWorkflowRunController = null;
      }
    },
  };
}
